/**
 * Singles -- the two deductions every player learns first, and between them the
 * entire Easy tier.
 */

import { CELLS, EMPTY, cellName, type Digit } from "../grid";
import { digitsOf, maskOf, popcount, soleDigit } from "../candidates";
import { unitName } from "../constraints";
import type { Context, Step, Technique } from "./types";

/**
 * Hidden single: within one unit, a digit has only one cell left to go in.
 *
 * Listed before naked single because it is what a player actually spots while
 * scanning a grid, and because SER rates it easier (1.5 against 2.3).
 */
export const hiddenSingle: Technique = {
  id: "hidden-single",
  name: "Hidden Single",
  tier: "easy",
  ser: 1.5,
  find({ board, candidates, cs }: Context): Step | null {
    for (const constraint of cs.constraints) {
      let placed = 0;
      for (const cell of constraint.cells) {
        if (board[cell] !== EMPTY) placed |= maskOf(board[cell]!);
      }

      for (const digit of digitsOf(~placed & 0b111111111)) {
        let target = -1;
        let count = 0;
        for (const cell of constraint.cells) {
          if (candidates[cell]! & maskOf(digit)) {
            target = cell;
            if (++count > 1) break;
          }
        }
        if (count === 1) {
          return {
            technique: "hidden-single",
            pattern: [target],
            digits: [digit],
            placements: [{ cell: target, digit }],
            eliminations: [],
            explanation:
              `Hidden Single: in ${unitName(constraint)}, ${digit} has only one cell left ` +
              `it can go in, so ${cellName(target)} must be ${digit}.`,
          };
        }
      }
    }
    return null;
  },
};

/** Naked single: a cell has exactly one candidate left. */
export const nakedSingle: Technique = {
  id: "naked-single",
  name: "Naked Single",
  tier: "easy",
  ser: 2.3,
  find({ board, candidates }: Context): Step | null {
    for (let cell = 0; cell < CELLS; cell++) {
      if (board[cell] !== EMPTY) continue;
      if (popcount(candidates[cell]!) !== 1) continue;
      const digit: Digit = soleDigit(candidates[cell]!);
      return {
        technique: "naked-single",
        pattern: [cell],
        digits: [digit],
        placements: [{ cell, digit }],
        eliminations: [],
        explanation:
          `Naked Single: every digit except ${digit} already appears in ` +
          `${cellName(cell)}'s row, column or box, so it must be ${digit}.`,
      };
    }
    return null;
  },
};
